import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import DiscoverWidget from "@/components/DiscoverWidget";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Calendar, MapPin, Radio, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SPORTS = [
    { id: "all", name: "All Sports" },
    { id: "football", name: "Football" },
    { id: "basketball", name: "Basketball" },
    { id: "boxing", name: "Boxing" },
    { id: "athletics", name: "Athletics" },
];

// East Africa Time formatter
const formatEAT = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch {
        return "";
    }
};

const formatEATTime = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch {
        return "";
    }
};

const formatEATDateKey = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-CA", {
            timeZone: "Africa/Dar_es_Salaam",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    } catch {
        return "";
    }
};

const formatEATDateLabel = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            weekday: "long",
            day: "numeric",
            month: "long",
        });
    } catch {
        return "";
    }
};

const SchedulesManagement = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [matches, setMatches] = useState([]);
    const [liveFixtures, setLiveFixtures] = useState([]);
    const [sport, setSport] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sport]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: 50,
                status: "scheduled",
                enabled_only: false,
            });
            if (sport !== "all") params.set("sport", sport);
            const [internalResp, espnResp] = await Promise.all([
                axios.get(`${API}/matches?${params.toString()}`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${API}/live-scores?window=upcoming${sport === "basketball" ? "&sport=basketball" : sport === "football" ? "&sport=soccer" : ""}`),
            ]);
            setMatches(internalResp.data || []);
            setLiveFixtures(espnResp.data?.matches || []);
        } finally {
            setLoading(false);
        }
    };

    // Merge manual + ESPN upcoming fixtures, then group by EAT date
    const combined = [
        ...matches.map((m) => ({
            source: "manual",
            id: m.id,
            home_team: m.home_team,
            away_team: m.away_team,
            home_abbr: "",
            away_abbr: "",
            competition_name: m.competition_name || m.competition_id,
            venue: m.venue,
            match_date: m.match_date,
            sport: m.sport,
            is_enabled: m.is_enabled !== false,
        })),
        ...liveFixtures
            .filter((lf) => lf.state === "pre")
            .map((lf) => ({
                source: "espn",
                id: lf.id,
                espn_event_id: lf.id,
                competition_id: lf.competition_id,
                home_team: lf.home_name,
                away_team: lf.away_name,
                home_abbr: lf.home_abbr,
                away_abbr: lf.away_abbr,
                competition_name: lf.competition_name,
                venue: lf.venue,
                match_date: lf.date,
                sport: lf.sport || "soccer",
                is_enabled: true,
            })),
    ];

    combined.sort((a, b) => new Date(a.match_date) - new Date(b.match_date));

    // Group by EAT date
    const grouped = combined.reduce((acc, m) => {
        const key = formatEATDateKey(m.match_date);
        if (!acc[key]) acc[key] = { label: formatEATDateLabel(m.match_date), items: [] };
        acc[key].items.push(m);
        return acc;
    }, {});

    return (
        <AdminLayout title="Fixtures">
            <div className="space-y-6" data-testid="schedules-management">
                {/* Discover fixtures from web — type team / pick league chip */}
                <DiscoverWidget defaultKind="fixture" />
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Calendar className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">
                                Match Fixtures
                            </h1>
                            <p className="text-white/50 text-sm">
                                Upcoming fixtures in East African Time (EAT / UTC+3). Each fixture links to a Swahili pre-match preview with analysis, team news, H2H, and AI prediction.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={sport} onValueChange={setSport}>
                            <SelectTrigger className="w-40 bg-subtle border-border text-white h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SPORTS.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={() => navigate("/admin/matches")}
                            className="bg-electric hover:bg-vodaredDark text-white"
                            data-testid="add-fixture-btn"
                        >
                            <Plus className="h-4 w-4 mr-1" /> Add Fixture
                        </Button>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="text-center py-16 text-white/40">Loading schedules...</div>
                ) : Object.keys(grouped).length === 0 ? (
                    <Card className="bg-paper border-border p-12 text-center">
                        <Calendar className="h-12 w-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">No upcoming fixtures.</p>
                        <p className="text-white/40 text-xs mt-1">
                            Try another sport, or add a fixture manually.
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(grouped).map(([key, g]) => (
                            <div key={key} data-testid={`schedule-day-${key}`}>
                                <div className="flex items-center gap-3 mb-3 pb-2 border-b border-border">
                                    <div className="w-1 h-5 bg-electric rounded" />
                                    <h2 className="font-heading font-bold text-sm text-white uppercase tracking-wider">
                                        {g.label}
                                    </h2>
                                    <span className="text-white/40 text-xs">
                                        {g.items.length} fixtures
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {g.items.map((m, idx) => (
                                        <Card
                                            key={m.id}
                                            className="bg-paper border-border p-4 hover:border-electric/40 transition-colors"
                                            data-testid={`schedule-${idx}`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <Badge
                                                        variant="outline"
                                                        className="border-border text-white/70 text-[10px] uppercase"
                                                    >
                                                        {m.competition_name}
                                                    </Badge>
                                                    {m.source === "manual" && (
                                                        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px] uppercase">
                                                            Manual
                                                        </Badge>
                                                    )}
                                                    {m.source === "espn" && (
                                                        <Badge className="bg-electric/10 text-electric border-electric/30 text-[10px] uppercase">
                                                            <Radio className="h-2.5 w-2.5 mr-0.5" /> Live Feed
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="font-mono text-electric font-bold">
                                                    {formatEATTime(m.match_date)} EAT
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between text-white">
                                                <span className="font-semibold">{m.home_team}</span>
                                                <span className="text-white/40 mx-3 text-xs">vs</span>
                                                <span className="font-semibold text-right">{m.away_team}</span>
                                            </div>
                                            {m.venue && (
                                                <div className="mt-2 flex items-center gap-1 text-white/50 text-xs">
                                                    <MapPin className="h-3 w-3" /> {m.venue}
                                                </div>
                                            )}
                                            <div className="mt-2 text-white/40 text-xs">
                                                {formatEAT(m.match_date)} · {m.sport}
                                            </div>
                                            {m.source === "espn" && m.espn_event_id && (
                                                <a
                                                    href={`/ratiba/${m.espn_event_id}?league=${m.competition_id}&sport=${m.sport === "basketball" ? "basketball" : "soccer"}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-2 inline-flex items-center gap-1 text-electric hover:text-white text-xs font-medium pt-2 border-t border-border w-full justify-center transition-colors"
                                                    data-testid={`fixture-preview-link-${idx}`}
                                                >
                                                    Angalia Uchambuzi &amp; Utabiri (SW) →
                                                </a>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default SchedulesManagement;
